import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { assessmentService } from '../../api';
import { Link } from 'react-router-dom';
import { ClipboardCheck, ArrowRight, LayoutGrid, List } from 'lucide-react';
import toast from 'react-hot-toast';
import ListPageHeader from '../../components/list/ListPageHeader';
import ListPagination from '../../components/list/ListPagination';
import ListEmptyState from '../../components/list/ListEmptyState';
import ListLoadingState from '../../components/list/ListLoadingState';
import DataRow, { DataRowList } from '../../components/list/DataRow';
import StatusPill from '../../components/list/StatusPill';
import { ShortCardGrid, ListSection } from '../../components/list/ShortCard';
import { AssessmentShortCard } from '../../components/list/ShortCardVariants';
import { parsePaginated, DEFAULT_LIST_PAGE_SIZE } from '../../utils/listHelpers';

function AssessmentList() {
    const [assessments, setAssessments] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(12);
    const [viewMode, setViewMode] = useState('grid');

    const fetchAssessments = useCallback(async () => {
        setLoading(true);
        try {
            const res = await assessmentService.getAssessments({ page, page_size: pageSize });
            const { results, count } = parsePaginated(res);
            setAssessments(results);
            setTotalCount(count);
        } catch {
            toast.error('Failed to load assessments');
        } finally {
            setLoading(false);
        }
    }, [page, pageSize]);

    useEffect(() => {
        fetchAssessments();
    }, [fetchAssessments]);

    const viewToggle = (
        <div className="ux-list-view-toggle">
            <button type="button" className={viewMode === 'grid' ? 'is-active' : ''} onClick={() => setViewMode('grid')} aria-label="Grid view">
                <LayoutGrid size={18} />
            </button>
            <button type="button" className={viewMode === 'list' ? 'is-active' : ''} onClick={() => setViewMode('list')} aria-label="List view">
                <List size={18} />
            </button>
        </div>
    );

    return (
        <DashboardLayout>
            <main className="page-container content-width ux-list-page">
                <ListPageHeader
                    kicker="Certification Track"
                    kickerIcon={ClipboardCheck}
                    title="Your Assessments"
                    description="Complete course assessments to unlock certificates. Practice assessments help you prepare without blocking completion."
                    actions={viewToggle}
                />

                {loading ? (
                    <ListLoadingState label="Loading assessments…" />
                ) : assessments.length === 0 ? (
                    <ListEmptyState icon={ClipboardCheck} title="No assessments yet" description="No assessments have been assigned to you." />
                ) : viewMode === 'grid' ? (
                    <ListSection title="Ready to take" description="Choose an assessment to begin.">
                        <ShortCardGrid>
                            {assessments.map((assessment, index) => (
                                <AssessmentShortCard key={assessment.id} assessment={assessment} index={index} />
                            ))}
                        </ShortCardGrid>
                    </ListSection>
                ) : (
                    <DataRowList>
                        {assessments.map((assessment, index) => (
                            <DataRow
                                key={assessment.id}
                                index={index}
                                leading={<div className="ux-row-thumb"><ClipboardCheck size={20} /></div>}
                                title={assessment.title}
                                subtitle={assessment.course_title || assessment.course_name || (assessment.course ? 'Course assessment' : 'Practice assessment')}
                                badges={<StatusPill status={assessment.status || 'pending'} label={(assessment.status || 'pending').toUpperCase()} />}
                                actions={(
                                    <Link to={`/assessments/${assessment.id}/take`} className="ux-btn-sm ux-btn-primary" style={{ textDecoration: 'none' }}>
                                        Start <ArrowRight size={14} />
                                    </Link>
                                )}
                            />
                        ))}
                    </DataRowList>
                )}

                {!loading && assessments.length > 0 && (
                    <ListPagination
                        page={page}
                        pageSize={pageSize}
                        totalCount={totalCount}
                        onPageChange={setPage}
                        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                        pageSizes={[8, 12, 24]}
                    />
                )}
            </main>
        </DashboardLayout>
    );
}

export default AssessmentList;
